<template>
  <div class="slide-carousel">
    <span v-show="current > 0" @click="goTo(current - 1)">
      <slot name="prev" />
    </span>
    <div
      class="slide-carousel__stage-outer"
      :class="{ 'is-dragging': dragging }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
      @dragstart.prevent
    >
      <div class="slide-carousel__stage" :style="stageStyle">
        <div
          v-for="(item, index) in items"
          :key="index"
          class="slide-carousel__item"
        >
          <slot :item="item" :index="index" />
        </div>
      </div>
    </div>
    <div v-if="items.length > 1" class="slide-carousel__dots">
      <button
        v-for="(item, index) in items"
        :key="index"
        type="button"
        class="slide-carousel__dot"
        :class="{ active: index === current }"
        :aria-label="`Show slide ${index + 1}`"
        @click="goTo(index)"
      >
        <span></span>
      </button>
    </div>
    <span v-show="current < items.length - 1" @click="goTo(current + 1)">
      <slot name="next" />
    </span>
  </div>
</template>

<script>
// px of horizontal drag needed to change slide
const DRAG_THRESHOLD = 40;

// jQuery-free replacement for vue-owl-carousel (one slide visible, no loop)
export default {
  name: 'SlideCarousel',
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    current: 0,
    dragging: false,
    dragStartX: 0,
    dragOffset: 0
  }),
  computed: {
    stageStyle() {
      return {
        transform: `translateX(calc(${-100 * this.current}% + ${
          this.dragOffset
        }px))`,
        transition: this.dragging ? 'none' : 'transform 0.25s ease'
      };
    }
  },
  methods: {
    goTo(index) {
      this.current = Math.max(0, Math.min(index, this.items.length - 1));
    },
    onPointerDown(event) {
      // primary button only (touch and pen report 0)
      if (event.button !== 0) {
        return;
      }
      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragOffset = 0;
    },
    onPointerMove(event) {
      if (!this.dragging) {
        return;
      }
      const offset = event.clientX - this.dragStartX;
      const stage = event.currentTarget;
      // capture only once it's a drag so plain clicks inside slides still work
      if (Math.abs(offset) > 5 && !stage.hasPointerCapture(event.pointerId)) {
        stage.setPointerCapture(event.pointerId);
      }
      const pastEdge =
        (offset > 0 && this.current === 0) ||
        (offset < 0 && this.current === this.items.length - 1);
      // resist pulling past either end
      this.dragOffset = pastEdge ? offset / 3 : offset;
    },
    onPointerUp() {
      if (!this.dragging) {
        return;
      }
      if (this.dragOffset <= -DRAG_THRESHOLD) {
        this.goTo(this.current + 1);
      } else if (this.dragOffset >= DRAG_THRESHOLD) {
        this.goTo(this.current - 1);
      }
      this.dragging = false;
      this.dragOffset = 0;
    }
  }
};
</script>

<style lang="scss">
// unscoped: slot content carries the parent's scope id
.slide-carousel__stage-outer {
  position: relative;
  overflow: hidden;
  touch-action: pan-y;
  cursor: grab;
  user-select: none;

  &.is-dragging {
    cursor: grabbing;
  }
}

.slide-carousel__stage {
  display: flex;
}

.slide-carousel__item {
  flex: 0 0 100%;
  min-width: 0;
  min-height: 1px;
}

// same specificity as owl's `.owl-carousel .owl-item img`
.slide-carousel .slide-carousel__item img {
  display: block;
  width: 100%;
}

.slide-carousel__dots {
  margin-top: 10px;
  text-align: center;
  -webkit-tap-highlight-color: transparent;
}

.slide-carousel__dot {
  display: inline-block;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  span {
    display: block;
    width: 10px;
    height: 10px;
    margin: 5px 7px;
    border-radius: 30px;
    background: #d6d6d6;
    transition: opacity 0.2s ease;
  }

  &.active span,
  &:hover span {
    background: #869791;
  }
}
</style>
